/**
 * ConduttoreFields — F-003.2 Phase 2 (IMPL-P2-002) + F-005 Phase 5 (IMPL-P5-004).
 *
 * Mirror di LocatoreFields con prefisso `conduttore.*`. Field group per il
 * conduttore (step 1 del wizard). Riusa CFInput da features/profilo tramite
 * Controller wrapper. Tutti i campi si collegano al FormProvider parent via
 * useFormContext.
 *
 * F-005: i campi contestati ricevono ring amber + sr-only hint via
 * `useErrorFields()` (vedi LocatoreFields per il pattern).
 *
 * F-004 (Phase 3, IMPL-P3-005): prop additiva `namePrefix` (default
 * `'conduttore'`) per riusare in blocco questo field group sotto un path RHF
 * diverso — la cessione ha bisogno del NUOVO inquilino sotto `nuovoInquilino.*`.
 * I chiamanti F-003/F-005 esistenti (`ContractWizard`, `ContractDenseForm`,
 * correction flow) non passano la prop e restano invariati (comportamento
 * identico byte-per-byte: stessi path, stessi id DOM, stesse lookup errori,
 * stessa integrazione `useErrorFields`). Il ring "contestato" F-005 resta
 * ancorato al path reale (`${namePrefix}.…`).
 *
 * Props:
 *  - showTooltips: true nel wizard (F-003.2), false nel form denso (F-003.3)
 *  - namePrefix: radice RHF del party (default `'conduttore'`; `'nuovoInquilino'`
 *    per la cessione F-004)
 */
import { Controller, useFormContext } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CFInput } from '@/features/profilo/components/CFInput';
import { cn } from '@/lib/utils';
import { useErrorFields } from '../../hooks/useErrorFields';
import {
  CONTESTED_HINT_TEXT,
  CONTESTED_RING_CLASS,
  contestedHintId,
} from './contestedFieldUtils';

export interface ConduttoreFieldsProps {
  showTooltips?: boolean;
  /**
   * Radice del path RHF del party. Default `'conduttore'` (invariato per i
   * chiamanti F-003/F-005). Override a `'nuovoInquilino'` per la cessione F-004.
   */
  namePrefix?: string;
}

export function ConduttoreFields({
  showTooltips: _showTooltips = false,
  namePrefix = 'conduttore',
}: ConduttoreFieldsProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<FieldValues>();

  // Lookup errori sul path del party (dotted-path safe).
  const con = errors[namePrefix] as
    | {
        nome?: { message?: string };
        cognome?: { message?: string };
        indirizzo?: {
          via?: { message?: string };
          civico?: { message?: string };
          cap?: { message?: string };
          comune?: { message?: string };
          provincia?: { message?: string };
        };
      }
    | undefined;
  const errorFields = useErrorFields();

  // Prefisso DOM stabile: id univoci anche quando due istanze coesistono
  // (es. conduttoreUscente vs nuovoInquilino — anche se qui l'uscente è
  // read-only e non usa questo componente).
  const domPrefix = namePrefix.replace(/\./g, '-');

  const cfPath = `${namePrefix}.codiceFiscale`;
  const nomePath = `${namePrefix}.nome`;
  const cognomePath = `${namePrefix}.cognome`;
  const viaPath = `${namePrefix}.indirizzo.via`;
  const civicoPath = `${namePrefix}.indirizzo.civico`;
  const capPath = `${namePrefix}.indirizzo.cap`;
  const comunePath = `${namePrefix}.indirizzo.comune`;
  const provinciaPath = `${namePrefix}.indirizzo.provincia`;

  const isCfContested = errorFields.includes(cfPath);
  const isNomeContested = errorFields.includes(nomePath);
  const isCognomeContested = errorFields.includes(cognomePath);

  return (
    <div className="space-y-4">
      {/* Codice Fiscale — usa CFInput via Controller */}
      <div>
        <Label htmlFor={`${domPrefix}-cf`}>Codice fiscale *</Label>
        <Controller
          name={cfPath}
          control={control}
          render={({ field, fieldState }) => (
            <CFInput
              id={`${domPrefix}-cf`}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
              cfType="PF"
              className={cn(isCfContested && CONTESTED_RING_CLASS)}
              aria-describedby={isCfContested ? contestedHintId(cfPath) : undefined}
            />
          )}
        />
        {isCfContested && (
          <span id={contestedHintId(cfPath)} className="sr-only">
            {CONTESTED_HINT_TEXT}
          </span>
        )}
      </div>

      {/* Nome + Cognome in griglia 2 colonne */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nome */}
        <div className="space-y-1">
          <Label htmlFor={`${domPrefix}-nome`}>Nome *</Label>
          <Controller
            name={nomePath}
            control={control}
            render={({ field }) => (
              <Input
                id={`${domPrefix}-nome`}
                {...field}
                value={field.value ?? ''}
                aria-invalid={!!con?.nome}
                aria-describedby={
                  con?.nome
                    ? `${domPrefix}-nome-error`
                    : isNomeContested
                      ? contestedHintId(nomePath)
                      : undefined
                }
                className={cn(isNomeContested && CONTESTED_RING_CLASS)}
              />
            )}
          />
          {con?.nome && (
            <p id={`${domPrefix}-nome-error`} className="text-xs text-destructive">
              {con.nome.message}
            </p>
          )}
          {isNomeContested && (
            <span id={contestedHintId(nomePath)} className="sr-only">
              {CONTESTED_HINT_TEXT}
            </span>
          )}
        </div>

        {/* Cognome */}
        <div className="space-y-1">
          <Label htmlFor={`${domPrefix}-cognome`}>Cognome</Label>
          <Controller
            name={cognomePath}
            control={control}
            render={({ field }) => (
              <Input
                id={`${domPrefix}-cognome`}
                {...field}
                value={field.value ?? ''}
                aria-invalid={!!con?.cognome}
                aria-describedby={
                  con?.cognome
                    ? `${domPrefix}-cognome-error`
                    : isCognomeContested
                      ? contestedHintId(cognomePath)
                      : undefined
                }
                className={cn(isCognomeContested && CONTESTED_RING_CLASS)}
              />
            )}
          />
          {con?.cognome && (
            <p id={`${domPrefix}-cognome-error`} className="text-xs text-destructive">
              {con.cognome.message}
            </p>
          )}
          {isCognomeContested && (
            <span id={contestedHintId(cognomePath)} className="sr-only">
              {CONTESTED_HINT_TEXT}
            </span>
          )}
        </div>
      </div>

      {/* Indirizzo — 5 campi in griglia */}
      <div>
        <p className="text-sm font-medium mb-2">Indirizzo</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Via */}
          <div className="space-y-1">
            <Label htmlFor={`${domPrefix}-via`}>Via / Piazza *</Label>
            <Controller
              name={viaPath}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${domPrefix}-via`}
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!con?.indirizzo?.via}
                  aria-describedby={con?.indirizzo?.via ? `${domPrefix}-via-error` : undefined}
                />
              )}
            />
            {con?.indirizzo?.via && (
              <p id={`${domPrefix}-via-error`} className="text-xs text-destructive">
                {con.indirizzo.via.message}
              </p>
            )}
          </div>

          {/* Civico */}
          <div className="space-y-1">
            <Label htmlFor={`${domPrefix}-civico`}>Civico *</Label>
            <Controller
              name={civicoPath}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${domPrefix}-civico`}
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!con?.indirizzo?.civico}
                  aria-describedby={con?.indirizzo?.civico ? `${domPrefix}-civico-error` : undefined}
                />
              )}
            />
            {con?.indirizzo?.civico && (
              <p id={`${domPrefix}-civico-error`} className="text-xs text-destructive">
                {con.indirizzo.civico.message}
              </p>
            )}
          </div>

          {/* CAP */}
          <div className="space-y-1">
            <Label htmlFor={`${domPrefix}-cap`}>CAP *</Label>
            <Controller
              name={capPath}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${domPrefix}-cap`}
                  maxLength={5}
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!con?.indirizzo?.cap}
                  aria-describedby={con?.indirizzo?.cap ? `${domPrefix}-cap-error` : undefined}
                />
              )}
            />
            {con?.indirizzo?.cap && (
              <p id={`${domPrefix}-cap-error`} className="text-xs text-destructive">
                {con.indirizzo.cap.message}
              </p>
            )}
          </div>

          {/* Comune */}
          <div className="space-y-1">
            <Label htmlFor={`${domPrefix}-comune`}>Comune *</Label>
            <Controller
              name={comunePath}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${domPrefix}-comune`}
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!con?.indirizzo?.comune}
                  aria-describedby={con?.indirizzo?.comune ? `${domPrefix}-comune-error` : undefined}
                />
              )}
            />
            {con?.indirizzo?.comune && (
              <p id={`${domPrefix}-comune-error`} className="text-xs text-destructive">
                {con.indirizzo.comune.message}
              </p>
            )}
          </div>

          {/* Provincia */}
          <div className="space-y-1">
            <Label htmlFor={`${domPrefix}-provincia`}>Provincia *</Label>
            <Controller
              name={provinciaPath}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${domPrefix}-provincia`}
                  maxLength={2}
                  className="uppercase"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  aria-invalid={!!con?.indirizzo?.provincia}
                  aria-describedby={
                    con?.indirizzo?.provincia ? `${domPrefix}-provincia-error` : undefined
                  }
                />
              )}
            />
            {con?.indirizzo?.provincia && (
              <p id={`${domPrefix}-provincia-error`} className="text-xs text-destructive">
                {con.indirizzo.provincia.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
